import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { NwsLogo } from "@/components/NwsLogo";
import { useAuth, useLogin } from "@/lib/auth";
import { ApiError } from "@/lib/api";

export default function Login() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Already logged in? Bounce out of /login.
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/dashboard"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    try {
      await login.mutateAsync({ email: email.trim(), password });
      const from = (location.state as { from?: string } | null)?.from;
      nav(from ?? "/dashboard", { replace: true });
    } catch {
      // The mutation already exposes login.error to the form; nothing to do.
    }
  }

  let errorText: string | null = null;
  if (login.isError) {
    const err = login.error;
    errorText = err instanceof ApiError && err.status === 401
      ? "Invalid email or password."
      : "Login failed. Try again in a moment.";
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/5 via-background to-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex items-center gap-3">
          <NwsLogo className="h-9 w-9 text-primary" />
          <div>
            <div className="text-lg font-semibold tracking-tight">Tyrael</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Security Operations Center
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-base font-semibold">Sign in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Use your nws® SOC credentials.
          </p>

          <form onSubmit={onSubmit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="you@nrg.ro"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-foreground">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {errorText && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                {errorText}
              </div>
            )}

            <button
              type="submit"
              disabled={login.isPending}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {login.isPending ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <div className="text-center text-[11px] text-muted-foreground">
          Confidential — internal use only.
        </div>
      </div>
    </div>
  );
}
